export interface StructuredSummary {
  keyPoints: string[];
  objections: string[];
  nextStep: string | null;
  raw: string;
}

const HEADERS = {
  keyPoints: ['key points', 'puntos clave', 'puntos importantes', 'highlights'],
  objections: ['objections', 'objeciones', 'concerns'],
  nextStep: ['next step', 'next steps', 'próximo paso', 'siguiente paso', 'proximo paso'],
};

function tryParseJson(input: string): StructuredSummary | null {
  const trimmed = input.trim();
  if (!trimmed.startsWith('{')) return null;

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const keyPoints = normalizeArray(
      parsed.keyPoints ?? parsed.key_points ?? parsed.highlights ?? parsed.puntos_clave,
    );
    const objections = normalizeArray(parsed.objections ?? parsed.concerns ?? parsed.objeciones);
    const nextStep = normalizeString(
      parsed.nextStep ?? parsed.next_step ?? parsed.next_steps ?? parsed.siguiente_paso,
    );

    if (!keyPoints.length && !objections.length && !nextStep) return null;

    return { keyPoints, objections, nextStep, raw: input };
  } catch {
    return null;
  }
}

function normalizeArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter((v): v is string => Boolean(v));
  }
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()];
  }
  return [];
}

function normalizeString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function matchHeader(line: string, options: readonly string[]): boolean {
  const lower = line
    .toLowerCase()
    .replace(/[*_:#\-\s]+/g, ' ')
    .trim();
  return options.some((opt) => lower.startsWith(opt));
}

function parseMarkdownSections(input: string): StructuredSummary | null {
  const lines = input.split(/\r?\n/);
  const sections: { keyPoints: string[]; objections: string[]; nextStep: string[] } = {
    keyPoints: [],
    objections: [],
    nextStep: [],
  };
  let current: keyof typeof sections | null = null;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (matchHeader(line, HEADERS.keyPoints)) {
      current = 'keyPoints';
      const inline = stripHeader(line, HEADERS.keyPoints);
      if (inline) sections.keyPoints.push(inline);
      continue;
    }
    if (matchHeader(line, HEADERS.objections)) {
      current = 'objections';
      const inline = stripHeader(line, HEADERS.objections);
      if (inline) sections.objections.push(inline);
      continue;
    }
    if (matchHeader(line, HEADERS.nextStep)) {
      current = 'nextStep';
      const inline = stripHeader(line, HEADERS.nextStep);
      if (inline) sections.nextStep.push(inline);
      continue;
    }

    if (!current) continue;

    const item = line.replace(/^[-*•]+\s*/, '').trim();
    if (item) sections[current].push(item);
  }

  if (!sections.keyPoints.length && !sections.objections.length && !sections.nextStep.length) {
    return null;
  }

  return {
    keyPoints: sections.keyPoints,
    objections: sections.objections,
    nextStep: sections.nextStep.join(' ').trim() || null,
    raw: input,
  };
}

function stripHeader(line: string, headers: readonly string[]): string {
  let cleaned = line.replace(/[*_#]+/g, '').trim();
  for (const header of headers) {
    const re = new RegExp(`^${header}\\s*:\\s*`, 'i');
    if (re.test(cleaned)) {
      cleaned = cleaned.replace(re, '').trim();
      break;
    }
  }
  return cleaned;
}

export function parseSummary(summary: string | null | undefined): StructuredSummary | null {
  if (!summary || !summary.trim()) return null;

  const json = tryParseJson(summary);
  if (json) return json;

  return parseMarkdownSections(summary);
}
